import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { leadService } from '../../services/leadService';
import { isLeadScrubImmediateResult, type LeadScrubJob, type LeadStatus } from '../../types/lead';
import { formatLeadCarrier, formatLeadLineType } from '../../utils/leadLookupDisplay';

function statusLabel(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function isActiveJob(job: LeadScrubJob | null): boolean {
  return job?.status === 'queued' || job?.status === 'running';
}

export type LeadScrubKind = 'phone_lookup' | 'dnc';

interface PhoneLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  availableStatuses: LeadStatus[];
  kind?: LeadScrubKind;
  onComplete?: () => void;
}

const COPY: Record<
  LeadScrubKind,
  { title: string; body: string; start: string; done: string; fail: string; already: string }
> = {
  phone_lookup: {
    title: 'Phone Lookup',
    body: 'Scrub selected statuses through RealValidito. Saves line type (mobile / landline / VoIP) and carrier on each lead. This does not run on import or during calls.',
    start: 'Phone lookup started',
    done: 'Phone lookup finished',
    fail: 'Phone lookup failed',
    already: 'A lookup job is already running',
  },
  dnc: {
    title: 'DNC',
    body: 'Scrub selected statuses against the RealValidito DNC list. Hits are marked Do Not Call (federal DNC and TCPA litigators). This does not run on import or during calls.',
    start: 'DNC scrub started',
    done: 'DNC scrub finished',
    fail: 'DNC scrub failed',
    already: 'A DNC job is already running',
  },
};

const LEAD_TIMEZONES = ['Eastern', 'Central', 'Mountain', 'Pacific', 'Unknown'] as const;

const PhoneLookupModal: React.FC<PhoneLookupModalProps> = ({
  isOpen,
  onClose,
  campaignId,
  availableStatuses,
  kind = 'phone_lookup',
  onComplete,
}) => {
  const copy = COPY[kind];
  const statusOptions = useMemo(() => {
    const list = availableStatuses.length > 0 ? availableStatuses : (['new'] as LeadStatus[]);
    return [...list].sort();
  }, [availableStatuses]);

  const [selected, setSelected] = useState<string[]>([]);
  const [selectedTimezones, setSelectedTimezones] = useState<string[]>([...LEAD_TIMEZONES]);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState<LeadScrubJob | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const defaults = statusOptions.includes('new' as LeadStatus) ? ['new'] : [];
    setSelected(defaults);
    setSelectedTimezones([...LEAD_TIMEZONES]);
  }, [isOpen, statusOptions]);

  const loadLatest = useCallback(async () => {
    try {
      const latest =
        kind === 'dnc'
          ? await leadService.getLatestDncScrub(campaignId)
          : await leadService.getLatestPhoneLookup(campaignId);
      setJob(latest);
    } catch (error) {
      console.error('Failed to load scrub job', error);
    }
  }, [campaignId, kind]);

  useEffect(() => {
    if (!isOpen) return;
    void loadLatest();
  }, [isOpen, loadLatest]);

  useEffect(() => {
    if (!isOpen || !isActiveJob(job) || !job) return;
    const timer = window.setInterval(async () => {
      try {
        const next =
          kind === 'dnc'
            ? await leadService.getDncScrubJob(campaignId, job.id)
            : await leadService.getPhoneLookupJob(campaignId, job.id);
        setJob(next);
        if (next.status === 'completed') {
          toast.success(
            `${copy.done}. Updated ${next.updatedLeads.toLocaleString()} leads, skipped ${next.skippedLeads.toLocaleString()}.`
          );
          onComplete?.();
        } else if (next.status === 'failed') {
          toast.error(next.errorMessage || copy.fail);
        }
      } catch (error) {
        console.error('Failed to poll scrub job', error);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [isOpen, campaignId, kind, copy.done, copy.fail, job?.id, job?.status, onComplete]);

  const toggleStatus = (status: string) => {
    setSelected((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleTimezone = (timezone: string) => {
    setSelectedTimezones((prev) =>
      prev.includes(timezone) ? prev.filter((z) => z !== timezone) : [...prev, timezone]
    );
  };

  const handleStart = async () => {
    if (selected.length === 0 || selectedTimezones.length === 0 || starting || isActiveJob(job)) return;
    setStarting(true);
    try {
      const payload = { statuses: selected, timezones: selectedTimezones };
      const started =
        kind === 'dnc'
          ? await leadService.startDncScrub(campaignId, payload)
          : await leadService.startPhoneLookup(campaignId, payload);
      if (isLeadScrubImmediateResult(started)) {
        const first = started.leads?.[0];
        if (
          kind === 'phone_lookup' &&
          started.leads?.length === 1 &&
          first &&
          (first.lineType || first.carrierName || first.carrier)
        ) {
          toast.success(
            `Lookup saved: ${formatLeadCarrier(first.carrierName, first.carrier)} · ${formatLeadLineType(first.lineType)}`
          );
        } else {
          toast.success(
            `${copy.done}. Updated ${started.updatedLeads.toLocaleString()} leads, skipped ${started.skippedLeads.toLocaleString()}.`
          );
        }
        onComplete?.();
        return;
      }
      setJob(started);
      toast.success(started.totalLeads === 0 ? 'No matching leads to scrub' : copy.start);
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { error?: { message?: string; details?: { job?: LeadScrubJob } }; message?: string };
        };
      };
      if (err.response?.status === 409 && err.response.data?.error?.details?.job) {
        setJob(err.response.data.error.details.job);
        toast.error(copy.already);
      } else {
        toast.error(err.response?.data?.error?.message || err.response?.data?.message || copy.fail);
      }
    } finally {
      setStarting(false);
    }
  };

  const progressPercent =
    job && job.totalLeads > 0
      ? Math.min(100, Math.round((job.processedLeads / job.totalLeads) * 100))
      : job?.status === 'completed'
        ? 100
        : 0;
  const running = isActiveJob(job);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={copy.title} size="md">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{copy.body}</p>

        <fieldset disabled={running || starting}>
          <legend className="text-sm font-medium text-foreground mb-2">Lead statuses</legend>
          <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto rounded-md border border-border p-3">
            {statusOptions.map((status) => {
              const id = `${kind}-${status}`;
              return (
                <label key={status} htmlFor={id} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    id={id}
                    type="checkbox"
                    className="rounded border-border"
                    checked={selected.includes(status)}
                    onChange={() => toggleStatus(status)}
                  />
                  {statusLabel(status)}
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset disabled={running || starting}>
          <legend className="text-sm font-medium text-foreground mb-2">Timezones</legend>
          <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3">
            {LEAD_TIMEZONES.map((timezone) => {
              const id = `${kind}-tz-${timezone}`;
              return (
                <label key={timezone} htmlFor={id} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    id={id}
                    type="checkbox"
                    className="rounded border-border"
                    checked={selectedTimezones.includes(timezone)}
                    onChange={() => toggleTimezone(timezone)}
                  />
                  {timezone}
                </label>
              );
            })}
          </div>
        </fieldset>

        {job && (
          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium capitalize">{job.status}</span>
            </div>
            <div className="h-2 rounded bg-muted overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${progressPercent}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">
              {job.processedLeads.toLocaleString()} / {job.totalLeads.toLocaleString()} processed
              {job.updatedLeads > 0 ? ` · ${job.updatedLeads.toLocaleString()} updated` : ''}
              {job.skippedLeads > 0 ? ` · ${job.skippedLeads.toLocaleString()} skipped` : ''}
            </p>
            {job.errorMessage && <p className="text-xs text-destructive">{job.errorMessage}</p>}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-border">
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleStart()}
            loading={starting}
            disabled={selected.length === 0 || selectedTimezones.length === 0 || running || starting}
          >
            {running ? 'Running…' : 'Start'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PhoneLookupModal;
